import React, { useState } from "react";
import { TTag } from "types/tag";

interface FilterRecipientsModalProps {
  tags: TTag[];
  isOpen: boolean;
  onClose: () => void;
  includeZipCodes: number[];
  setIncludeZipCodes: (zip: number[]) => void;
  excludeZipCodes: number[];
  setExcludeZipCodes: (zip: number[]) => void;
  includeCities: string[];
  setIncludeCities: (cities: string[]) => void;
  excludeCities: string[];
  setExcludeCities: (cities: string[]) => void;
  includeStates: string[];
  setIncludeStates: (states: string[]) => void;
  excludeStates: string[];
  setExcludeStates: (states: string[]) => void;
  includeTags: number[];
  setIncludeTags: (tags: number[]) => void;
  excludeTags: number[];
  setExcludeTags: (tags: number[]) => void;
}

const FilterRecipientsModal: React.FC<FilterRecipientsModalProps> = ({
  tags,
  isOpen,
  onClose,
  includeZipCodes,
  setIncludeZipCodes,
  excludeZipCodes,
  setExcludeZipCodes,
  includeCities,
  setIncludeCities,
  excludeCities,
  setExcludeCities,
  includeStates,
  setIncludeStates,
  excludeStates,
  setExcludeStates,
  includeTags,
  setIncludeTags,
  excludeTags,
  setExcludeTags,
}) => {
  const [zipInput, setZipInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [stateInput, setStateInput] = useState("");

  if (!isOpen) return null;

  const addFilter = (value: string, setFunction: Function, currentList: any[], resetFunction: Function) => {
    if (value && !currentList.includes(value)) {
      setFunction([...currentList, value]);
      resetFunction("");
    }
  };

  const toggleTag = (tagId: number, setFunction: Function, currentTags: number[]) => {
    setFunction(
      currentTags.includes(tagId)
        ? currentTags.filter((id) => id !== tagId)
        : [...currentTags, tagId]
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Filter Recipients</h2>
          <button className="text-gray-600 hover:text-gray-900" onClick={onClose}>✖</button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto flex-grow">
          {/* ZIP Code Filters */}
          <label className="block text-gray-700 font-medium mb-2">ZIP Codes</label>
          <div className="flex items-center gap-2 mb-4">
            <input type="text" className="w-full p-2 border rounded-lg" value={zipInput} onChange={(e) => setZipInput(e.target.value)} placeholder="Enter ZIP Code" />
            <button className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg" onClick={() => addFilter(zipInput, setIncludeZipCodes, includeZipCodes, setZipInput)}>Include</button>
            <button className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg" onClick={() => addFilter(zipInput, setExcludeZipCodes, excludeZipCodes, setZipInput)}>Exclude</button>
          </div>

          {/* City Filters */}
          <label className="block text-gray-700 font-medium mb-2">Cities</label>
          <div className="flex items-center gap-2 mb-4">
            <input type="text" className="w-full p-2 border rounded-lg" value={cityInput} onChange={(e) => setCityInput(e.target.value)} placeholder="Enter City" />
            <button className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg" onClick={() => addFilter(cityInput, setIncludeCities, includeCities, setCityInput)}>Include</button>
            <button className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg" onClick={() => addFilter(cityInput, setExcludeCities, excludeCities, setCityInput)}>Exclude</button>
          </div>

          {/* State Filters */}
          <label className="block text-gray-700 font-medium mb-2">States</label>
          <div className="flex items-center gap-2 mb-4">
            <input type="text" className="w-full p-2 border rounded-lg" value={stateInput} onChange={(e) => setStateInput(e.target.value)} placeholder="Enter State" />
            <button className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg" onClick={() => addFilter(stateInput, setIncludeStates, includeStates, setStateInput)}>Include</button>
            <button className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg" onClick={() => addFilter(stateInput, setExcludeStates, excludeStates, setStateInput)}>Exclude</button>
          </div>

          {/* Tags - Include */}
          <label className="block text-blue-600 font-semibold mt-4 mb-2">Tags - Include</label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag.id}
                className={`px-3 py-1 rounded-lg text-sm font-semibold border ${includeTags.includes(Number(tag.id)) ? "bg-green-300 border-green-500" : "bg-gray-200 border-gray-300"}`}
                onClick={() => toggleTag(Number(tag.id), setIncludeTags, includeTags)}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Tags - Exclude */}
          <label className="block text-red-600 font-semibold mt-4 mb-2">Tags - Exclude</label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag.id}
                className={`px-3 py-1 rounded-lg text-sm font-semibold border ${excludeTags.includes(Number(tag.id)) ? "bg-red-300 border-red-500" : "bg-gray-200 border-gray-300"}`}
                onClick={() => toggleTag(Number(tag.id), setExcludeTags, excludeTags)}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-4">
          <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 bg-green-500 text-white rounded-lg" onClick={onClose}>Apply</button>
        </div>
      </div>
    </div>
  );
};

export default FilterRecipientsModal;
